import {
  IonContent,
  IonCheckbox,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonButton,
  IonSpinner,
  useIonToast,
} from "@ionic/react";
import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Header from "src/components/Header";
import asignacionRecursos from "src/services/asignacionRecursos";
import PuntoServicioAPI from "src/services/puntoDeServicio";

type PuntoServicio = {
  id: number;
  ps_nombre: string;
  ps_objeto?: string;
  ps_codigo?: string;
};

type Recurso = {
  resourceNo: string;
  resourceName: string;
  codDimension?: string;
  nombreDimension?: string;
  raw?: any;
};

const AsignacionRecursos: React.FC = () => {
  const history = useHistory();
  const [puntos, setPuntos] = useState<PuntoServicio[]>([]);
  const [psSeleccionado, setPsSeleccionado] = useState<string>("");
  const [psSeleccionadoNombre, setPsSeleccionadoNombre] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [recursos, setRecursos] = useState<Recurso[]>([]);
  const [cargandoPuntos, setCargandoPuntos] = useState<boolean>(false);
  const [cargandoRecursos, setCargandoRecursos] = useState<boolean>(false);
  const [errorPuntos, setErrorPuntos] = useState<string | undefined>(undefined);
  const [seleccionados, setSeleccionados] = useState<Record<string, boolean>>({});
  const [mostrarMover, setMostrarMover] = useState<boolean>(false);
  const [searchMover, setSearchMover] = useState<string>("");
  const [puntosMover, setPuntosMover] = useState<PuntoServicio[]>([]);
  const [cargandoPuntosMover, setCargandoPuntosMover] = useState<boolean>(false);
  const [errorPuntosMover, setErrorPuntosMover] = useState<string | undefined>(undefined);
  const [psDestino, setPsDestino] = useState<PuntoServicio | null>(null);
  const [present] = useIonToast();

  useEffect(() => {
    const cargar = async () => {
      if (searchValue.length < 3) {
        setErrorPuntos(undefined);
        setPuntos([]);
        return;
      }
      setCargandoPuntos(true);
      try {
        const res = await PuntoServicioAPI.listar(searchValue);
        setPuntos(res.data?.datos || []);
        setErrorPuntos(undefined);
      } catch (error: any) {
        setPuntos([]);
        setErrorPuntos(error?.response?.data?.error || "No se pudieron cargar los puntos de servicio");
      } finally {
        setCargandoPuntos(false);
      }
    };
    cargar();
  }, [searchValue]);

  const cargarRecursos = async (psObjeto: string) => {
    setRecursos([]);
    setSeleccionados({});
    setMostrarMover(false);
    setPsDestino(null);
    if (!psObjeto) return;
    setCargandoRecursos(true);
    try {
      const res = await asignacionRecursos.listarRecursos(psObjeto);
      setRecursos(res.data?.datos || []);
    } catch (error: any) {
      present({
        message: error?.response?.data?.error || "No se pudieron cargar los recursos",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setCargandoRecursos(false);
    }
  };

  useEffect(() => {
    const cargar = async () => {
      if (searchMover.length < 3) {
        setErrorPuntosMover(undefined);
        setPuntosMover([]);
        return;
      }
      setCargandoPuntosMover(true);
      try {
        const res = await PuntoServicioAPI.listar(searchMover);
        setPuntosMover(res.data?.datos || []);
        setErrorPuntosMover(undefined);
      } catch (error: any) {
        setPuntosMover([]);
        setErrorPuntosMover(error?.response?.data?.error || "No se pudieron cargar los puntos de servicio");
      } finally {
        setCargandoPuntosMover(false);
      }
    };
    cargar();
  }, [searchMover]);

  const toggleSeleccion = (id: string) => {
    setSeleccionados((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const seleccionadosIds = Object.keys(seleccionados).filter((k) => seleccionados[k]);

  const recursosNormalizados = useMemo(() => {
    return recursos.map((r: any) => {
      const resourceNo = r.resourceNo || r.no || r.codActFijo || "";
      const resourceName = r.resourceName || r.name || "";
      const codDimension = r.codDimension || r.CodDimension;
      const nombreDimension = r.nombreDimension || r.NombreDimension;
      return {
        ...r,
        resourceNo,
        resourceName,
        codDimension,
        nombreDimension,
      } as Recurso;
    });
  }, [recursos]);

  const recursosFiltrados = recursosNormalizados;

  const seleccionarPunto = (p: PuntoServicio) => {
    const val = (p.ps_objeto || p.ps_codigo || "").trim();
    if (!val) return;
    setPsSeleccionado(val);
    setPsSeleccionadoNombre(p.ps_nombre || "");
    cargarRecursos(val);
  };

  return (
    <IonPage>
      <Header titulo="Asignacion de Recursos" />
      <IonContent id="asignacion-recursos" className="ion-padding">
        <IonItem>
          <IonLabel position="stacked">Buscar Punto de Servicio</IonLabel>
          <IonInput
            value={searchValue}
            onIonChange={(e) => setSearchValue(e.detail.value || "")}
            placeholder="Buscar Punto de servicio"
          />
        </IonItem>

        {cargandoPuntos && (
          <div style={{ display: "flex", justifyContent: "center", padding: "16px" }}>
            <IonSpinner name="crescent" />
          </div>
        )}

        {!cargandoPuntos && searchValue.length > 0 && searchValue.length < 3 && (
          <IonItem lines="none">
            <IonLabel>Ingrese al menos 3 letras para filtrar puntos de servicio.</IonLabel>
          </IonItem>
        )}

        {!cargandoPuntos && errorPuntos && (
          <IonItem lines="none">
            <IonLabel>{errorPuntos}</IonLabel>
          </IonItem>
        )}

        {!cargandoPuntos && searchValue.length >= 3 && puntos.length === 0 && !errorPuntos && (
          <IonItem lines="none">
            <IonLabel>{`No se encontraron puntos de servicio con el filtro "${searchValue}"`}</IonLabel>
          </IonItem>
        )}

        {!cargandoPuntos && puntos.length > 0 && (
          <IonList>
            {puntos.map((p) => {
              const val = p.ps_objeto || p.ps_codigo || "";
              return (
                <IonItem key={p.id || val} button onClick={() => seleccionarPunto(p)}>
                  <IonLabel>
                    <h2>{p.ps_nombre}</h2>
                    <p>{val}</p>
                  </IonLabel>
                </IonItem>
              );
            })}
          </IonList>
        )}

        {psSeleccionado && (
          <IonItem lines="none">
            <IonLabel>
              <strong>PS seleccionado:</strong> {psSeleccionadoNombre || "-"} ({psSeleccionado})
            </IonLabel>
          </IonItem>
        )}

        {cargandoRecursos && (
          <div style={{ display: "flex", justifyContent: "center", padding: "16px" }}>
            <IonSpinner name="crescent" />
          </div>
        )}

        {!cargandoRecursos && psSeleccionado && recursosFiltrados.length === 0 && (
          <IonItem lines="none">
            <IonLabel>No hay recursos para mostrar.</IonLabel>
          </IonItem>
        )}

        {!cargandoRecursos && recursosNormalizados.length > 0 && (
          <IonList>
            {recursosNormalizados.map((r) => (
              <IonItem key={`${r.resourceNo}-${r.codDimension || ""}`}>
                <IonCheckbox
                  slot="start"
                  checked={!!seleccionados[r.resourceNo]}
                  onIonChange={() => toggleSeleccion(r.resourceNo)}
                />
                <IonLabel>
                  <h2>{r.resourceName || "-"}</h2>
                  <p>Codigo: {r.resourceNo || "-"}</p>
                  <p>PS: {r.nombreDimension || "-"}</p>
                </IonLabel>
              </IonItem>
            ))}
          </IonList>
        )}

        {recursosFiltrados.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <IonButton
              expand="block"
              disabled={seleccionadosIds.length === 0}
              onClick={() => setMostrarMover((v) => !v)}
            >
              Mover recursos seleccionados
            </IonButton>
          </div>
        )}

        {mostrarMover && (
          <>
            <IonItem>
              <IonLabel position="stacked">Buscar Punto de Servicio destino</IonLabel>
              <IonInput
                value={searchMover}
                onIonChange={(e) => setSearchMover(e.detail.value || "")}
                placeholder="Buscar Punto de servicio"
              />
            </IonItem>

            {cargandoPuntosMover && (
              <div style={{ display: "flex", justifyContent: "center", padding: "16px" }}>
                <IonSpinner name="crescent" />
              </div>
            )}

            {!cargandoPuntosMover && searchMover.length > 0 && searchMover.length < 3 && (
              <IonItem lines="none">
                <IonLabel>Ingrese al menos 3 letras para filtrar puntos de servicio.</IonLabel>
              </IonItem>
            )}

            {!cargandoPuntosMover && errorPuntosMover && (
              <IonItem lines="none">
                <IonLabel>{errorPuntosMover}</IonLabel>
              </IonItem>
            )}

            {!cargandoPuntosMover && searchMover.length >= 3 && puntosMover.length === 0 && !errorPuntosMover && (
              <IonItem lines="none">
                <IonLabel>{`No se encontraron puntos de servicio con el filtro "${searchMover}"`}</IonLabel>
              </IonItem>
            )}

            {!cargandoPuntosMover && puntosMover.length > 0 && (
              <IonList>
                {puntosMover.map((p) => {
                  const val = p.ps_objeto || p.ps_codigo || "";
                  return (
                    <IonItem key={p.id || val} button onClick={() => setPsDestino(p)}>
                      <IonLabel>
                        <h2>{p.ps_nombre}</h2>
                        <p>{val}</p>
                      </IonLabel>
                    </IonItem>
                  );
                })}
              </IonList>
            )}

            {psDestino && (
              <IonItem lines="none">
                <IonLabel>
                  <strong>Destino:</strong> {psDestino.ps_nombre || "-"} ({psDestino.ps_objeto || psDestino.ps_codigo})
                </IonLabel>
              </IonItem>
            )}

            <div style={{ marginTop: 12 }}>
              <IonButton
                expand="block"
                disabled={seleccionadosIds.length === 0 || !psDestino}
                onClick={async () => {
                  try {
                    const psDestinoVal = (psDestino?.ps_objeto || psDestino?.ps_codigo || "").trim();
                    const psOrigenVal = (psSeleccionado || "").trim();
                    if (!psDestinoVal || !psOrigenVal) {
                      present({ message: "Seleccione un punto de servicio destino.", duration: 3000, color: "warning" });
                      return;
                    }
                    const payload = {
                      recursos: seleccionadosIds,
                      ps_origen: psOrigenVal,
                      ps_destino: psDestinoVal,
                    };
                    const res = await asignacionRecursos.moverRecursos(payload);
                    const ok = res.data?.ok || [];
                    const errores = res.data?.errores || [];
                    if (ok.length > 0) {
                      present({ message: `Se movieron ${ok.length} recurso(s).`, duration: 4000, color: "success" });
                      // Sin recarga/refresh extra
                    }
                    if (errores.length > 0) {
                      present({ message: `Fallaron ${errores.length} recurso(s).`, duration: 5000, color: "warning" });
                    }
                  } catch (error: any) {
                    present({
                      message: error?.response?.data?.error || "No se pudieron mover los recursos",
                      duration: 5000,
                      color: "danger",
                    });
                  }
                }}
              >
                Confirmar movimiento
              </IonButton>
            </div>
          </>
        )}
      </IonContent>
    </IonPage>
  );
};

export default AsignacionRecursos;
